import type { ICreationAlarm, ICreationAlarmClassification, IEditAlarmPayload } from '@/interfaces/api/alarm'
import { buildRansackQuery } from '@/utils/alarm-listing/ransack-query'
import ApiService from '../api.service'
import getCurrentCompanyId from '../company.service'

interface IAlarmListingDynamicFilter {
  field: string
  operator: string
  value: string | number | boolean
  valueType?: 'string' | 'number' | 'date' | 'boolean'
}

const AlarmResource = {
  createAlarm: (params: ICreationAlarm) => ApiService.post(`/v1/companies/${getCurrentCompanyId()}/alarms`, params),
  createClassification: (params: ICreationAlarmClassification) => ApiService.post(`/v1/companies/${getCurrentCompanyId()}/classification_types`, params),
  getClassifications: () => ApiService.get(`/v1/companies/${getCurrentCompanyId()}/classification_types`),
  /** `dateRangeQuery`: `start_date=...&end_date=...` (ex.: `formatPeriodDate`) */
  fetchMetrics: (
    dateRangeQuery: string,
    isClassification: boolean,
    sensorIds: number[],
    alarmTypeKeys: string[],
    classificationTypeIds: number[],
    shiftId: number | '',
    groupId: number | '',
  ) => {
    const params = new URLSearchParams(dateRangeQuery)
    params.set('classified', String(isClassification))
    if (sensorIds.length) params.set('sensor_ids', sensorIds.join(','))
    if (alarmTypeKeys.length) params.set('alarm_types', alarmTypeKeys.join(','))
    if (classificationTypeIds.length) params.set('classification_type_ids', classificationTypeIds.join(','))
    if (shiftId !== '' && shiftId != null) params.set('shift_id', String(shiftId))
    if (groupId !== '' && groupId != null) params.set('group_id', String(groupId))
    return ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarms/metrics?${params.toString()}`)
  },
  removeAlarm: (id: number) => ApiService.delete(`v1/companies/${getCurrentCompanyId()}/alarms/${id}`),
  getOpenAlarms: () =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarms/open`),
  getAlarms: ({
    start_date,
    end_date,
    sensor_id,
    alarm_type,
    include_group_alarms,
    items = 200,
  }: {
    start_date: string
    end_date: string
    sensor_id?: number
    alarm_type?: string
    include_group_alarms?: boolean
    items?: number
  }) => {
    const params = new URLSearchParams({
      start_date,
      end_date,
      items: String(items),
    })
    if (sensor_id != null) params.set('sensor_id', String(sensor_id))
    if (alarm_type) params.set('alarm_type', alarm_type)
    if (include_group_alarms) params.set('include_group_alarms', 'true')
    return ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarms?${params.toString()}`)
  },
  getAlarmsPages: (
    page: number,
    items: number,
    sort: string,
    sort_direction: string,
    classified: boolean | '',
    is_group: boolean,
    filters: IAlarmListingDynamicFilter[] = [],
  ) => {
    const ransackQuery = buildRansackQuery(filters)
    const baseQuery = `page=${page}&items=${items}&sort=${sort}&sort_direction=${sort_direction}&classified=${classified}&is_group=${is_group}`
    const query = ransackQuery ? `${baseQuery}&${ransackQuery}` : baseQuery
    return ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarms?${query}`)
  },
  editAlarm: (alarmId: number, payload: IEditAlarmPayload) =>
    ApiService.put(`/v1/companies/${getCurrentCompanyId()}/alarms/${alarmId}`, payload),

  getAlarm: (alarmId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarms/${alarmId}`),

  getAlarmInterruptSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_interrupt_sensors/${profileId}`),

  getAlarmCriticalTemperatureSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_critical_temperature_sensors/${profileId}`),

  getAlarmLowLoadSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_low_load_sensors/${profileId}`),

  getAlarmPowerFactorSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_power_factor_sensors/${profileId}`),

  getAlarmDefrostSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_defrost_sensors/${profileId}`),

  getAlarmPeakHourSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_peak_hour_sensors/${profileId}`),

  getAlarmEmptyMixerSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_empty_mixer_sensors/${profileId}`),

  getAlarmOutsideRegularSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_outside_regular_sensors/${profileId}`),

  getAlarmNoDataSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_no_data_sensors/${profileId}`),

  getAlarmDoorOpenSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_door_open_sensors/${profileId}`),

  getAlarmOvervoltageSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_overvoltage_sensors/${profileId}`),

  getAlarmOutOfCycleSensorProfile: (profileId: number) =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_out_of_cycle_sensors/${profileId}`),

  getAlarmTypeConfigsByCompany: () =>
    ApiService.get(`/v1/companies/${getCurrentCompanyId()}/alarm_type_configs/list_by_company`),
}

export default AlarmResource
